import { arrayMove } from '@dnd-kit/sortable';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import { Disclosure, DisclosurePanel } from 'react-aria-components';

import { Button, Flex, Label, Table, Text } from '@/components/core';
import { AddIcon, ChevronIcon } from '@/components/icons';
import { FieldLogo } from '@/components/logos';
import { AddFieldButton } from '@/components/ui';
import { FieldState, useModelConfigStore } from '@/store';

export type OnFieldItemClick = (field: FieldState) => void;

export type FieldCellProps = {
  field: FieldState;
  onFieldItemClick?: OnFieldItemClick;
};

const IFieldCell = ({ field, onFieldItemClick }: FieldCellProps) => {
  const setFields = useModelConfigStore((state) => state.setFields);
  const deleteField = useModelConfigStore((state) => state.deleteField);
  const duplicateField = useModelConfigStore((state) => state.duplicateField);

  const columns = useMemo<ColumnDef<FieldState>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: ({ row }) => <FieldCell field={row.original} onFieldItemClick={onFieldItemClick} />,
        size: Number.MAX_SAFE_INTEGER,
      },
    ],
    [onFieldItemClick]
  );

  const fieldName = useMemo(() => field.name ?? field.id, [field.id, field.name]);

  if (field.type === 'table' && field.fields) {
    return (
      <Disclosure defaultExpanded>
        <Flex
          col
          className="group/field-cell p-xs cursor-pointer items-start rounded-md border border-gray-300 bg-white shadow-xs hover:bg-gray-50"
        >
          <Flex variant="plain" className="items-center">
            <Button slot="trigger" className="-rotate-90 bg-transparent aria-expanded:rotate-0">
              <ChevronIcon rotation="down" />
            </Button>
            <Button variant="plain" onPress={() => onFieldItemClick?.(field)}>
              <Label>{fieldName}</Label>
            </Button>
          </Flex>

          <DisclosurePanel>
            <div className="p-xs">
              <Table
                className="p-md border-separate border-spacing-x-0 border-spacing-y-2 py-0"
                data={field.fields}
                columns={columns}
                isInnerTable={true}
                onRowIndexChange={(oldIndex, newIndex) =>
                  setFields(arrayMove(field.fields ?? [], oldIndex, newIndex), field.id)
                }
                hideHeader
                duplicateRowAction={(row) => duplicateField(row.original.id, field.id)}
                deleteRowAction={(row) => deleteField(row.original.id, field.id)}
              />
              <AddFieldButton parentId={field.id} variant="plain" className="ms-sm">
                <AddIcon />
                <Text>Add column</Text>
              </AddFieldButton>
            </div>
          </DisclosurePanel>
        </Flex>
      </Disclosure>
    );
  } else {
    return (
      <div
        onClick={() => onFieldItemClick?.(field)}
        className="group/field-cell px-xs py-xs flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white shadow-xs hover:bg-gray-50"
      >
        <FieldLogo field={field} size="sm" />
        <Text className="grow">{fieldName}</Text>
      </div>
    );
  }
};

export const FieldCell = memo(IFieldCell) as typeof IFieldCell;
